import React, { useEffect, useState } from "react";
import "@arcgis/core/assets/esri/themes/light/main.css";
import axios from "./axios";

// Your custom components
import MapView from "./components/MapView";
import FilterPanel from "./components/FilterPanel";
import { isValidCoord } from "./utils/coordinateUtils";

const EsriMap = () => {
  const [view, setView] = useState(null);

  // Access & elevation data
  const [accessData, setAccessData] = useState(null);
  const [elevationData, setElevationData] = useState(null);

  const [buildings, setBuildings] = useState([]);
  const [apartaments, setApartments] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [polygonsLevels, setPolygonsLevels] = useState([]);
  const [polygonsListHierarchyLevels, setPolygonsListHierarchyLevels] = useState([]);
  const [threedObjListHierarchyLevels, setthreedObjListHierarchyLevels] = useState([]);
  const [mapObjects, setMapObjects] = useState([]);
  const [threeDCategories, setThreeDCategories] = useState([]);
  const [objectCategories, setObjectCategories] = useState([]);

  // Checkboxes
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Filtered data sets
  const [filteredBuildings, setFilteredBuildings] = useState([]);
  const [filteredApartments, setFilteredApartments] = useState([]);
  const [polygonsVisibility, setPolygonsVisibility] = useState(true);
  const [objectVisibility, setObjectsVisibility] = useState(true);

  // NEW STATES for lists
  const [listsData, setListsData] = useState([]);
  const [filteredMapObjects, setFilteredMapObjects] = useState([]);

  /**************************************
   * 1) Fetch Access + Elevation
   **************************************/
  useEffect(() => {
    axios
      .get("/eharta-access/all")
      .then((res) => {
        const adata = res?.data?.data?.access || null;
        setAccessData(adata);
      })
      .catch((err) => console.error(err));

    axios
      .get("/eharta-elevation/all")
      .then((res) => {
        const edata = res?.data?.data?.access || null;
        setElevationData(edata);
      })
      .catch((err) => console.error(err));
  }, []);

  /**************************************
   * 2) Once we have access, fetch data
   **************************************/
  useEffect(() => {
    if (!accessData) return;
    // Buildings
    axios
      .get("/building/all")
      .then((res) => {
        const data = res?.data?.data || [];
        setBuildings(data);
        setFilteredBuildings(data);
      })
      .catch((err) => console.error(err));

    // Apartments (default call with empty body {})
    axios
      .post("/building-apartament/apartaments", {})
      .then((res) => {
        const data = res?.data?.data || [];
        setApartments(data);
        setFilteredApartments(data);
      })
      .catch((err) => console.error(err));

    // Polygons
    axios
      .post("/polygons/filter")
      .then((res) => {
        const data = res?.data?.data || [];
        setPolygons(data);
      })
      .catch((err) => console.error(err));

    // Polygons Filtering 3 Levels
    axios
      .get("/polygons/hierarchy")
      .then((res) => {
        const data = res?.data?.data || [];
        setPolygonsLevels(data);
      })
      .catch((err) => console.error(err));
    axios
      .get("/lists/list-polygon-hierarchy")
      .then((res) => {
        const data = res?.data?.data || [];
        setPolygonsListHierarchyLevels(data);
      })
      .catch((err) => console.error(err));
    axios
      .get("/lists/list-3dobject-hierarchy")
      .then((res) => {
        const data = res?.data?.data || [];
        setthreedObjListHierarchyLevels(data);
      })
      .catch((err) => console.error(err));

    // 3D Objects
    axios
      .get("/threeD-categories/all")
      .then((res) => {
        const data = res?.data?.data || [];
        setThreeDCategories(data);
      })
      .catch((err) => console.error(err));

    axios
      .post("/map-objects/all/filter")
      .then((res) => {
        const data = res?.data?.data || [];
        setMapObjects(data);
      })
      .catch((err) => console.error(err));

    // Building categories
    axios
      .get("/building-category/all")
      .then((res) => {
        const data = res?.data?.data || [];
        setObjectCategories(data);
      })
      .catch((err) => console.error(err));
  }, [accessData]);

  /**************************************
   * 2.1) Fetch lists/all separately
   **************************************/
  useEffect(() => {
    // You can fetch lists/all even if you don’t have accessData yet, or wait until you do.
    axios
      .get("/list-types/lists/all")
      .then((res) => {
        // Adjust based on your server’s response shape:
        setListsData(res.data?.data || []);
      })
      .catch((err) => console.error(err));
  }, []);

  /**************************************
   * 2.2) On select from lists, POST to get apartments
   **************************************/
  const fetchApartmentsBySelectedFilters = async (filters) => {
    const requestBody = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => Array.isArray(v) && v.length > 0)
    );

    try {
      const response = await axios.post("/building-apartament/apartaments", requestBody);
      const data = response?.data?.data || [];
      setApartments(data);
      setFilteredApartments(data);
    } catch (error) {
      console.error("Error fetching apartments for selected filters:", error);
    }
  };


  const fetchPolygonsBySelectedFilters = async (filters) => {
    // Create the request body, preserving both array and object values
    const requestBody = {};
  
    // Add polygon_id if it exists and is an array with values
    if (filters.polygon_id && Array.isArray(filters.polygon_id) && filters.polygon_id.length > 0) {
      requestBody.polygon_id = filters.polygon_id;
    }
  
    // Add polygon_type if it exists and is a non-empty object
    if (filters.polygon_type && typeof filters.polygon_type === "object" && Object.keys(filters.polygon_type).length > 0) {
      // Ensure each subfield (topLevel, keys, children) has values
      const cleanedPolygonType = {};
      if (filters.polygon_type.topLevel?.length > 0) {
        cleanedPolygonType.topLevel = filters.polygon_type.topLevel;
      }
      if (filters.polygon_type.keys?.length > 0) {
        cleanedPolygonType.keys = filters.polygon_type.keys;
      }
      if (filters.polygon_type.children?.length > 0) {
        cleanedPolygonType.children = filters.polygon_type.children;
      }
      if (Object.keys(cleanedPolygonType).length > 0) {
        requestBody.polygon_type = cleanedPolygonType;
      }
    }
  
    // Add polygon_type_id if it exists and is an array with values
    if (filters.polygon_type_id && Array.isArray(filters.polygon_type_id) && filters.polygon_type_id.length > 0) {
      requestBody.polygon_type_id = filters.polygon_type_id;
    }
  
    try {
      const response = await axios.post("/polygons/filter", requestBody);
      const data = response?.data?.data || [];
      setPolygons(data); // Update the state with filtered polygons
    } catch (error) {
      console.error("Error fetching polygons for selected filters:", error);
    }
  };




const fetchFilteredMapObjects = async (filters) => {
  try {
    const response = await axios.post("/map-objects/all/filter", filters);
    const data = response?.data?.data || [];
    setMapObjects(data);
    setFilteredMapObjects(data);
  } catch (error) {
    console.error("Error fetching filtered 3D objects:", error);
  }
};

  /**************************************
   * 3) Handle checkboxes
   **************************************/
  const handleCheckboxChange = (categoryId) => {
    setSelectedCategories((prevSelected) => {
      let updatedCategories = [...prevSelected];

      if (categoryId === "all" && updatedCategories.includes("withApartments")) {
        updatedCategories = updatedCategories.filter(
          (id) => id !== "withApartments"
        );
      }
  
      if (
        categoryId === "withApartments" &&
        updatedCategories.includes("all")
      ) {
        updatedCategories = updatedCategories.filter((id) => id !== "all");
      }

      if (updatedCategories.includes(categoryId)) {
        updatedCategories = updatedCategories.filter((id) => id !== categoryId);
      } else {
        updatedCategories.push(categoryId);
      }

      return updatedCategories;
    });
  };

  const filterBuildings = () => {
    let filteredBuildings = [...buildings];
    let filteredApartments = [...apartaments];
    const polygonsChecked = selectedCategories.includes("allPolygons");
    const objectsChecked = selectedCategories.includes("allObjects");
    const withAptsChecked = selectedCategories.includes("withApartments");
    const allChecked = selectedCategories.includes("all");

    const chosenCategories = objectCategories
      .map((cat) => cat.name)
      .filter((name) => selectedCategories.includes(name));

    // Handle "All Buildings"
    if (allChecked) {
      setFilteredBuildings(buildings);
      setFilteredApartments(apartaments);
      setPolygonsVisibility(polygonsChecked);
      setObjectsVisibility(objectsChecked);
      return;
    }

    // Step 1: Filter buildings with apartments if "Has Apartments" is checked
    let buildingsWithApartments = [];
    if (withAptsChecked) {
      buildingsWithApartments = buildings.filter((building) => {
        const floorIds = building.floorDetails?.map((fd) => fd.id) || [];
        return apartaments.some(
          (apartment) =>
            floorIds.includes(apartment.id) &&
            Array.isArray(apartment.coords) &&
            apartment.coords.length > 0 &&
            apartment.coords.every(isValidCoord)
        );
      });
    }

    // Step 2: Filter buildings by selected categories
    let categoryFilteredBuildings = [];
    if (chosenCategories.length > 0) {
      categoryFilteredBuildings = buildings.filter((building) =>
        chosenCategories.includes(building.buildingCategory)
      );
    }

    if (withAptsChecked && chosenCategories.length > 0) {
      filteredBuildings = buildingsWithApartments.filter((building) =>
        categoryFilteredBuildings.some((catBuilding) => catBuilding.id === building.id)
      );
    } else if (withAptsChecked) {
      filteredBuildings = buildingsWithApartments;
    } else if (chosenCategories.length > 0) {
      filteredBuildings = categoryFilteredBuildings;
    } else {
      filteredBuildings = [];
    }

    filteredApartments = apartaments.filter((apartment) => {
      return filteredBuildings.some((building) => {
        const floorIds = building.floorDetails?.map((fd) => fd.id) || [];
        return floorIds.includes(apartment.id);
      });
    });

    // setPolygonsVisibility(polygonsChecked);
    // setObjectsVisibility(objectsChecked);

    setFilteredBuildings(filteredBuildings);
    setFilteredApartments(filteredApartments);
  };

  /**************************************
   * 4) If user has no access, show an error
   **************************************/
  if (!accessData) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  

  /**************************************
   * 5) Render
   **************************************/
  return (
    <>
      {/* If you have a modal for apartments, place it here */}
      <div id="apartmentModal" className="modal" style={{ display: "none" }}>
        <div className="modal-content">
          <span
            className="close"
            onClick={() =>
              (document.getElementById("apartmentModal").style.display = "none")
            }
          >
            &times;
          </span>
          <div className="modal-body"></div>
        </div>
      </div>

      <MapView
        view={view}
        setView={setView}
        polygons={polygons}
        polygonsVisibility={polygonsVisibility}
        mapObjects={mapObjects}
        objectVisibility={objectVisibility}
        buildings={filteredBuildings}
        apartaments={filteredApartments}
        elevationData={elevationData}
      />

      <FilterPanel
        objectCategories={objectCategories}
        polygonsLevels={polygonsLevels}
        polygonsListHierarchyLevels={polygonsListHierarchyLevels}
        thredObjListHierarchyLevels={threedObjListHierarchyLevels}
        selectedCategories={selectedCategories}
        handleCheckboxChange={handleCheckboxChange}
        filterBuildings={filterBuildings}
        listsData={listsData}
        threeDCategories={threeDCategories}
        onFetchApartments={fetchApartmentsBySelectedFilters}
        onFetchPolygons={fetchPolygonsBySelectedFilters}
        onFetchMapObjects={fetchFilteredMapObjects}
      />
    </>
  );
};

export default EsriMap;
